"use client";

import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import { BaseLink } from "@components/core/Link";
import { Check, X } from "lucide-react";

import { getPriceForCurrency } from "@mw/shared/pricing";
import { getRegionLocale } from "@mw/shared/i18n";

import { FormattedNumber, FormattedHoursAndMinutes } from "./offers/shared";
import { useCurrency } from "@/app/_providers/currency";
import type { CurrencyUnit, FormattedValue } from "./pricing-value-types";

/** Maps CurrencyUnit to translation keys */
const UNIT_TRANSLATION_KEYS: Record<CurrencyUnit, string> = {
  per_hour: "per_hour",
  per_minute: "per_minute",
  per_minute_approx: "per_minute_approx",
  per_message: "per_message",
  per_1k_characters: "per_1k_characters",
  per_generation: "per_generation",
  flat: "flat",
  flat_approx: "flat_approx",
};

/**
 * Hook that returns a function to format prices with currency.
 * Removes trailing zeros for whole numbers.
 */
export const useFormatTierPrice = () => {
  const { currency } = useCurrency();
  const locale = useLocale();
  const regionLocale = getRegionLocale(locale);

  return (price: number) => {
    const convertedPrice = getPriceForCurrency(price, currency);
    return new Intl.NumberFormat(regionLocale, {
      style: "currency",
      currency: currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: convertedPrice % 1 === 0 ? 0 : 2,
    }).format(convertedPrice);
  };
};

interface PricingValueRendererProps {
  value: FormattedValue;
}

/** Renders a FormattedValue using appropriate formatting */
export const PricingValueRenderer = ({ value }: PricingValueRendererProps) => {
  const { currency } = useCurrency();
  const locale = useLocale();
  const regionLocale = getRegionLocale(locale);
  // Using ClientKeys namespace so translations are available on the client

  const t = useTranslations("PricingClientKeys.PricingUnits") as (
    key: string,
    params?: Record<string, unknown>,
  ) => string;

  switch (value.type) {
    case "unavailable":
      return null;

    case "number":
    case "characters":
      return <FormattedNumber number={value.value} />;

    case "currency": {
      if (value.value === undefined) return null;
      const convertedPrice = getPriceForCurrency(value.value, currency);
      const precision = value.precision ?? 2;
      // Round to specified precision for display
      const multiplier = Math.pow(10, precision);
      const roundedPrice = Math.round(convertedPrice * multiplier) / multiplier;
      const formattedValue = new Intl.NumberFormat(regionLocale, {
        style: "currency",
        currency: currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(roundedPrice);
      if (value.unit === "flat") return <>{formattedValue}</>;
      if (value.unit === "flat_approx") return <>~{formattedValue}</>;
      return <>{t(UNIT_TRANSLATION_KEYS[value.unit], { value: formattedValue })}</>;
    }

    case "hours":
      if (value.value === undefined || value.value === 0) return null;
      if (value.value === Infinity) return <>{t("unlimited")}</>;
      return <FormattedHoursAndMinutes hoursValue={value.value} />;

    case "minutes":
      if (value.value === undefined || value.value === 0) return null;
      if (value.value === Infinity) return <>{t("unlimited")}</>;
      return <>{t("minutes", { value: new Intl.NumberFormat(regionLocale).format(value.value) })}</>;

    case "minutes_approx":
      if (value.value === undefined || value.value === 0) return null;
      if (value.value === Infinity) return <>{t("unlimited")}</>;
      if (value.suffix === "none") return <>~{new Intl.NumberFormat(regionLocale).format(value.value)}</>;
      return <>{t("minutes_approx", { value: new Intl.NumberFormat(regionLocale).format(value.value) })}</>;

    case "text": {
      // Format any numeric params with locale-aware number formatting
      const formattedParams = value.params
        ? Object.fromEntries(
            Object.entries(value.params).map(([k, v]) => [
              k,
              typeof v === "number" ? new Intl.NumberFormat(regionLocale).format(v) : v,
            ]),
          )
        : undefined;
      return <>{t(value.textKey, formattedParams)}</>;
    }

    case "boolean":
      return value.value ? (
        <Check className='tw-w-4 tw-h-4 tw-text-green-600' />
      ) : (
        <X className='tw-w-4 tw-h-4 tw-text-gray-400' />
      );

    case "link":
      return (
        <BaseLink
          href={value.href}
          target={value.target}
          className='tw-text-current tw-text-2xs tw-underline tw-underline-offset-2 hover:tw-no-underline'
          style={{ fontSize: "0.75rem", lineHeight: "0.8rem" }}
        >
          {t(value.textKey)}
        </BaseLink>
      );

    default:
      return null;
  }
};
